import React, { useEffect, useState } from "react";
import { Image, Tag } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  UserOutlined,
  WarningOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import swal from "sweetalert";
import useGetFile from "../../hooks/useGetFile";
import useAxios from "../../hooks/useAxios";
import socket from "../../services/socket";

const WFO_INTERVIEW_REASON =
  "WFO Interview (In-Person, No Phygital Capture)";

const OTHER_NO_PHOTO_REASONS = [
  "Technical Issue - Photo Not Reflecting",
  "Telephonic Interview",
];

const ProfilePhotoMatchAgent = ({
  interview,
  candidate,
  agentFaceMatchStatus,
  setAgentFaceMatchStatus,
  agentNoRecordReason,
  setAgentNoRecordReason,
  recruiterFaceMatchResponse,
  setRecruiterFaceMatchResponse,
  autoRejectCandidate,
  candidatePhoto,
  error,
  isViewMode = false,
  onRefresh,
}) => {
  const workModal = String(candidate?.trf?.workModal || "WFO").toUpperCase();
  const isWfo = workModal === "WFO";

  const userToken = localStorage.getItem("user.token");
  const { getImage } = useGetFile(userToken);
  const api = useAxios();
  const [fetchedPhotoUrl, setFetchedPhotoUrl] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Real-time socket listener for instant verification update
  useEffect(() => {
    const candidateId = candidate?._id;
    if (!candidateId) return;

    const handleVerificationUpdate = async (data) => {
      const incomingId = data?.candidateId || data?._id;
      if (!incomingId || String(incomingId) === String(candidateId)) {
        console.log("[WebSocket] ProfilePhotoMatchAgent received real-time verification update:", data);
        if (data?.bestFrameImageUrl) {
          const url = data.bestFrameImageUrl;
          if (url.startsWith("http://") || url.startsWith("https://")) {
            setFetchedPhotoUrl(url);
          } else if (url.startsWith("data:")) {
            setFetchedPhotoUrl(url);
          } else if (url.length > 500) {
            setFetchedPhotoUrl(`data:image/jpeg;base64,${url}`);
          }
        }
        if (typeof onRefresh === "function") {
          await onRefresh();
        }
      }
    };

    socket.on("candidate_score_updated", handleVerificationUpdate);
    return () => {
      socket.off("candidate_score_updated", handleVerificationUpdate);
    };
  }, [candidate?._id, onRefresh]);

  useEffect(() => {
    const loadFetchedPhoto = async () => {
      const url = interview?.phygitalVerification?.bestFrameImageUrl;
      if (!url) {
        setFetchedPhotoUrl("");
        return;
      }
      if (url.startsWith("http://") || url.startsWith("https://")) {
        setFetchedPhotoUrl(url);
      } else if (url.startsWith("data:image/") || url.length > 500) {
        if (url.startsWith("data:")) {
          setFetchedPhotoUrl(url);
        } else {
          setFetchedPhotoUrl(`data:image/jpeg;base64,${url}`);
        }
      } else {
        const signedUrl = await getImage(url);
        setFetchedPhotoUrl(signedUrl);
      }
    };
    loadFetchedPhoto();
  }, [interview?.phygitalVerification?.bestFrameImageUrl]);

  useEffect(() => {
    if (agentFaceMatchStatus !== "Missing" || !isWfo) {
      return;
    }

    if (!agentNoRecordReason) {
      setAgentNoRecordReason(WFO_INTERVIEW_REASON);
    }
  }, [
    agentFaceMatchStatus,
    agentNoRecordReason,
    isWfo,
    setAgentNoRecordReason,
  ]);

  const handleFetchResult = async () => {
    try {
      setIsFetching(true);
      const res = await api.post("/phygital/fetch-verification-result", {
        candidateId: candidate?._id,
        interviewRound: interview?.type,
      }, {
        tokenType: "user"
      });
      const data = res.data;
      if (data.success) {
        swal("Success", "Verification result fetched successfully.", "success");
        if (typeof onRefresh === "function") {
          await onRefresh();
        } else {
          setTimeout(() => window.location.reload(), 1200);
        }
      } else {
        swal("Notice", data.error || "No new results found.", "info");
      }
    } catch (err) {
      swal("Error", "Failed to fetch verification result.", "error");
    } finally {
      setIsFetching(false);
    }
  };

  const handleOverrideMismatch = () => {
    swal({
      title: "Are you sure you want to continue?",
      text: "This action will lead to auto rejection of the candidate and the profile will be moved to the rejection pool. Are you sure you want to continue?",
      icon: "warning",
      buttons: ["Cancel", "OK"],
      dangerMode: true,
    }).then((willReject) => {
      if (willReject) {
        setRecruiterFaceMatchResponse("Mismatched");
        autoRejectCandidate("Mismatch", "Mismatched");
      }
    });
  };


  const handleRecruiterDecisionChange = (val) => {
    if (val === "Mismatched") {
      swal({
        title: "Are you sure you want to continue?",
        text: "This action will automatically reject the candidate and move them to the Rejected Pool. Are you sure you want to continue?",
        icon: "warning",
        buttons: ["Cancel", "OK"],
        dangerMode: true,
      }).then((willReject) => {
        if (willReject) {
          setRecruiterFaceMatchResponse("Mismatched");
          autoRejectCandidate("Mismatch", "Mismatched");
        } else {
          setRecruiterFaceMatchResponse("");
        }
      });
    } else {
      setRecruiterFaceMatchResponse(val);
    }
  };

  const displayRecruiterName = interview?.interviewer?.name || interview?.interviewer || "Recruiter";

  const hasPhygitalData = Boolean(
    interview?.finished ||
    interview?.phygitalVerification?.bestFrameImageUrl ||
    interview?.phygitalVerification?.submittedAt ||
    interview?.phygitalVerification?.meetingId ||
    interview?.phygitalVerification?.interviewStatus
  );

  return (
    <div className="card p-3 mb-1 border bg-white" style={{ borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <h6 className="font-weight-bold text-dark mb-0">AI Face Verification</h6>
        </div>
        <div className="d-flex align-items-center" style={{ gap: "6px" }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center"
            onClick={async () => {
              setIsRefreshing(true);
              try {
                if (typeof onRefresh === "function") {
                  await onRefresh();
                } else {
                  window.location.reload();
                }
              } catch (e) {
                console.error(e);
              } finally {
                setIsRefreshing(false);
              }
            }}
            disabled={isRefreshing || isFetching}
            title="Refresh Interview Component"
            style={{ padding: "4px 8px", fontSize: "12px", height: "31px", borderRadius: "4px" }}
          >
            <SyncOutlined spin={isRefreshing} />
          </button>
          {!isViewMode && (
            <button
              className="btn btn-sm btn-outline-primary font-weight-bold"
              onClick={handleFetchResult}
              disabled={isFetching || isRefreshing || hasPhygitalData}
              title={hasPhygitalData ? "Data already fetched from Phygital" : "Fetch details from Phygital"}
              style={{ fontSize: "12px", display: "flex", alignItems: "center" }}
            >
              {isFetching ? "Fetching..." : "Fetch Details"}
            </button>
          )}
        </div>
      </div>

      {/* Photo Comparison Panels */}
      <div className="d-flex justify-content-between mb-3" style={{ gap: "1rem" }}>
        {/* Left Side: Portal Master Photo */}
        <div className="text-center bg-white p-2 rounded border d-flex flex-column align-items-center" style={{ flex: 1 }}>
          <span className="d-block text-muted small font-weight-bold mb-2">Portal Master Photo</span>
          {candidatePhoto ? (
            <Image
              height={140}
              width={160}
              style={{ objectFit: "cover", borderRadius: "8px" }}
              src={candidatePhoto}
              alt="Portal Master"
            />
          ) : (
            <div
              className="d-flex align-items-center justify-content-center bg-secondary text-white rounded mx-auto"
              style={{ width: "100px", height: "100px", borderRadius: "50%" }}
            >
              <UserOutlined style={{ fontSize: "3rem" }} />
            </div>
          )}
        </div>

        {/* Right Side: Fetched Photo */}
        <div className="text-center bg-white p-2 rounded border d-flex flex-column align-items-center" style={{ flex: 1 }}>
          <span className="d-block text-muted small font-weight-bold mb-2">Fetched Photo</span>
          {fetchedPhotoUrl ? (
            <Image
              height={140}
              width={160}
              style={{ objectFit: "cover", borderRadius: "8px" }}
              src={fetchedPhotoUrl}
              alt="Fetched Photo"
            />
          ) : (
            <div
              className="d-flex flex-column align-items-center justify-content-center bg-light text-muted mx-auto border"
              style={{ width: "100px", height: "100px", borderRadius: "50%" }}
            >
              <WarningOutlined style={{ fontSize: "2rem" }} className="text-warning mb-1" />
              <span style={{ fontSize: "10px" }} className="font-weight-bold">No Captured Photo</span>
            </div>
          )}
        </div>
      </div>

      {/* AI Face Match Status Banners */}
      {agentFaceMatchStatus === "Matched" && (
        <div className="mb-3">
          <div
            className="d-flex align-items-center p-3 mb-2"
            style={{
              backgroundColor: "#f0fdf4",
              border: "1px solid #dcfce7",
              borderRadius: "8px",
            }}
          >
            <div
              className="d-flex justify-content-center align-items-center bg-success text-white"
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                marginRight: "16px",
              }}
            >
              <CheckOutlined style={{ fontSize: "14px" }} />
            </div>
            <div className="d-flex flex-column">
              <strong style={{ fontSize: "14px", color: "#1f2937" }}>
                AI Decision: Photo Match
              </strong>
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                Match Score: {interview?.phygitalVerification?.accuracyPercentage != null
                  ? `${interview.phygitalVerification.accuracyPercentage}%`
                  : "N/A"}
              </span>
            </div>
          </div>

          {((!interview?.finished && (interview?.status === "Selected" || interview?.status === "Rejected")) || !interview?.phygitalVerification?.interviewStatus || interview?.phygitalVerification?.interviewStatus === "no-response" || interview?.phygitalVerification?.interviewStatus === "failure") && (
            <div className="mt-2">
              <label className="small font-weight-bold text-dark mb-1">
                Recruiter Face Match Decision <span className="text-danger">*</span>
              </label>
              <select
                className="form-control form-control-sm"
                value={recruiterFaceMatchResponse}
                onChange={(e) => handleRecruiterDecisionChange(e.target.value)}
                disabled={isViewMode}
              >
                <option value="">-- Select Decision --</option>
                <option value="Matched">Photos Matched</option>
                <option value="Mismatched">Photos Mismatched</option>
              </select>
              {error?.recruiterFaceMatchResponse && (
                <span className="text-danger small mt-1 d-block">{error.recruiterFaceMatchResponse}</span>
              )}
            </div>
          )}

        </div>
      )}

      {agentFaceMatchStatus === "Mismatch" && (
        <div className="mb-3">
          <div
            className="d-flex align-items-center p-3 mb-2"
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fee2e2",
              borderRadius: "8px",
            }}
          >
            <div
              className="d-flex justify-content-center align-items-center bg-danger text-white"
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                marginRight: "16px",
              }}
            >
              <CloseOutlined style={{ fontSize: "14px" }} />
            </div>
            <div className="d-flex flex-column">
              <strong style={{ fontSize: "14px", color: "#1f2937" }}>
                AI Decision: Photo Mismatch
              </strong>
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                Match Score: {interview?.phygitalVerification?.accuracyPercentage != null
                  ? `${interview.phygitalVerification.accuracyPercentage}%`
                  : "N/A"}
              </span>
            </div>
          </div>

          {((!interview?.finished && (interview?.status === "Selected" || interview?.status === "Rejected")) || !interview?.phygitalVerification?.interviewStatus || interview?.phygitalVerification?.interviewStatus === "no-response" || interview?.phygitalVerification?.interviewStatus === "failure") && (
            <div>
              <label className="small font-weight-bold text-dark mb-1">
                Recruiter Face Match Decision <span className="text-danger">*</span>
              </label>
              <select
                className="form-control form-control-sm"
                value={recruiterFaceMatchResponse}
                onChange={(e) => handleRecruiterDecisionChange(e.target.value)}
                disabled={isViewMode}
              >
                <option value="">-- Select Decision --</option>
                <option value="Matched">Photos Matched</option>
                <option value="Mismatched">Photos Mismatched</option>
              </select>
              {error?.recruiterFaceMatchResponse && (
                <span className="text-danger small mt-1 d-block">{error.recruiterFaceMatchResponse}</span>
              )}
            </div>
          )}
        </div>
      )}

      {agentFaceMatchStatus === "Missing" && (
        <div className="mb-3">
          <div className="alert alert-warning p-2 d-flex align-items-center mb-2" style={{ gap: "8px" }}>
            <WarningOutlined style={{ fontSize: "16px" }} />
            <strong className="small text-dark">
              No interview photo found. Face verification could not be
              performed. Please select a reason below.
            </strong>
          </div>
          <div>
            <label className="small font-weight-bold text-dark mb-1">
              Reason for No Photo
              {!isWfo && <span className="text-danger"> *</span>}
            </label>
            <select
              className="form-control form-control-sm"
              value={agentNoRecordReason}
              onChange={(e) => setAgentNoRecordReason(e.target.value)}
              disabled={isViewMode}
            >
              {!isWfo && <option value="">-- Select Reason --</option>}
              <option value={WFO_INTERVIEW_REASON}>
                {WFO_INTERVIEW_REASON}
              </option>
              {OTHER_NO_PHOTO_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
            {error?.agentNoRecordReason && (
              <span className="text-danger small mt-1 d-block">{error.agentNoRecordReason}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePhotoMatchAgent;
